/**
 * Native-connector hooks: the shared catalog, per-agent connector list, and the
 * connect/disconnect/reconnect/rename lifecycle plus per-connector permission
 * toggles. Distinct from the agent-tool `permissions` module: these keys hang
 * off the connector namespace.
 */

import { hubQueryKeys } from "@lifter/hub-ui/browser-shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type AgentQueryOptions, useHubApi } from "./context";

// Connector Catalog hooks for the Connectors tab.

export function useConnectorCatalogQuery(options: AgentQueryOptions = {}) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.connectorCatalog(),
    queryFn: () => api.connectors.catalog(),
    enabled: options.enabled ?? true,
  });
}

export function useSetConnectorCredentialsMutation() {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      agentId: string;
      connectorId: string;
      profileName?: string;
      displayName?: string;
      fields: Record<string, string>;
    }) => api.connectors.setCredentials(payload),
    onSuccess: (_data, variables) => {
      // Gateway persists the credential and capability profile together.
      // Invalidate connector list and permissions so the connected profile
      // and source-owned tool catalog render immediately.
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.credentialsRoot() });
      queryClient.invalidateQueries({
        queryKey: hubQueryKeys.connectorList(variables.agentId),
      });
      queryClient.invalidateQueries({
        queryKey: hubQueryKeys.connectorPermissions(variables.agentId),
      });
    },
  });
}

export function useAgentConnectorsQuery(agentId: string, options: AgentQueryOptions = {}) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.connectorList(agentId),
    queryFn: () => api.connectors.list(agentId),
    enabled: options.enabled ?? true,
    // Gateway-side status flips (e.g. active → auth_required) don't push to the
    // Hub UI yet, so without a poll the badge stays stale until a mount or a
    // Hub-initiated mutation invalidates the cache. React Query pauses this
    // while the tab is backgrounded.
    refetchInterval: 30_000,
  });
}

export function useConnectorPermissionsQuery(agentId: string, options: AgentQueryOptions = {}) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.connectorPermissions(agentId),
    queryFn: () => api.connectors.permissions.get(agentId),
    enabled: options.enabled ?? true,
    refetchInterval: (query) =>
      query.state.data?.profiles.some((profile) => profile.discovery.status === "not_inspected")
        ? 30_000
        : false,
  });
}

export function useConnectMutation(agentId: string) {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { connectorId: string; profileName?: string; displayName?: string }) =>
      api.connectors.connect({ agentId, ...payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.connectorList(agentId) });
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.connectorPermissions(agentId) });
    },
  });
}

export function useDisconnectMutation(agentId: string) {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { connectorId: string; profileName: string }) =>
      api.connectors.disconnect({ agentId, ...payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.connectorList(agentId) });
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.connectorPermissions(agentId) });
    },
  });
}

export function useReconnectMutation(agentId: string) {
  const api = useHubApi();
  return useMutation({
    mutationFn: (payload: { connectorId: string; profileName: string; displayName?: string }) =>
      api.connectors.reconnect({ agentId, ...payload }),
  });
}

export function useRenameConnectorMutation(agentId: string) {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { connectorId: string; profileName: string; displayName: string }) =>
      api.connectors.rename({ agentId, ...payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.connectorList(agentId) });
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.connectorPermissions(agentId) });
    },
  });
}

export function useSetConnectorPermissionMutation(agentId: string) {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      scope: "profile" | "builtin" | "gateway-tool" | "browser" | "memory";
      serverId?: string;
      profileName?: string;
      sourceId?: string;
      toolId: string;
      state: "allow" | "ask" | "deny";
    }) => api.connectors.permissions.set({ agentId, ...payload }),
    // Return the invalidation so React Query awaits the refetch before the
    // mutation promise settles and useOptimisticPermissions drops the overlay entry.
    // Without the return the refetch is still in flight when the entry is
    // deleted, so resolveState falls back to the stale base and the toggle
    // flickers new→old→new for the whole Hub→Gateway roundtrip.
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.connectorPermissions(agentId) }),
    // On failure, refetch the authoritative value so the optimistic entry the
    // hook drops when the mutation promise settles rolls back to the server's
    // state — the rollback-via-invalidation the PermissionsPanel docstring promises.
    onError: () =>
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.connectorPermissions(agentId) }),
  });
}

// Instance connector administration (Settings → Connectors). Admin-only:
// every one of these 403s for anyone else, so the panel gates on authority
// before it queries.

export function useConnectorAdminQuery(options: AgentQueryOptions = {}) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.connectorAdmin(),
    queryFn: () => api.connectors.admin.list(),
    enabled: options.enabled ?? true,
  });
}

export function useSetConnectorEnabledMutation() {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { connectorType: string; enabled: boolean }) =>
      api.connectors.admin.setEnabled(input.connectorType, input.enabled),
    onSuccess: () => invalidateConnectorAdmin(queryClient),
  });
}

export function useSaveConnectorConfigMutation() {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      connectorType: string;
      clientId: string;
      clientSecret: string;
      appHandle?: string;
    }) =>
      api.connectors.admin.saveConfig(input.connectorType, {
        clientId: input.clientId,
        clientSecret: input.clientSecret,
        ...(input.appHandle ? { appHandle: input.appHandle } : {}),
      }),
    onSuccess: () => invalidateConnectorAdmin(queryClient),
  });
}

export function useDeleteConnectorConfigMutation() {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (connectorType: string) => api.connectors.admin.deleteConfig(connectorType),
    onSuccess: () => invalidateConnectorAdmin(queryClient),
  });
}

/**
 * An administration change moves what every agent's connect surface offers, so
 * the shared catalog goes too — a stale catalog would keep offering a connector
 * that was just turned off.
 */
function invalidateConnectorAdmin(queryClient: ReturnType<typeof useQueryClient>): void {
  queryClient.invalidateQueries({ queryKey: hubQueryKeys.connectorAdmin() });
  queryClient.invalidateQueries({ queryKey: hubQueryKeys.connectorCatalog() });
}
